import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { UserService } from '../services/user.service';
import { MatIconModule } from '@angular/material/icon';
import { LoaderService } from '../../../shared/services/loader.service';
import { NotificationService } from '../../../shared/services/notification.service';

@Component({
  selector: 'app-cambio-contrasena',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule],
  templateUrl: './cambio-contrasena.component.html',
  styleUrls: ['./cambio-contrasena.component.scss']
})
export class CambioContrasenaComponent {
  private router = inject(Router);
  private userService = inject(UserService);
  private loaderService = inject(LoaderService);
  private notificationService = inject(NotificationService);
  
  currentPassword = '';
  newPassword = '';
  confirmPassword = '';
  showCurrentPassword = false;
  showNewPassword = false;
  showConfirmPassword = false;
  attempts = 0;
  maxAttempts = 3;
  isBlocked = false;

  validatePassword(password: string): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    if (password.length < 8) errors.push('Mínimo 8 caracteres');
    if (!/[A-Z]/.test(password)) errors.push('Una mayúscula');
    if (!/[a-z]/.test(password)) errors.push('Una minúscula');
    if (!/\d/.test(password)) errors.push('Un número');
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) errors.push('Un carácter especial');
    if (password === this.currentPassword) errors.push('Debe ser diferente a la actual');
    
    return { isValid: errors.length === 0, errors };
  }



  hasSpecialChar(password: string): boolean {
    return /[!@#$%^&*(),.?":{}|<>]/.test(password);
  }

  hasUpperCase(password: string): boolean {
    return /[A-Z]/.test(password);
  }

  hasLowerCase(password: string): boolean {
    return /[a-z]/.test(password);
  }

  hasNumber(password: string): boolean {
    return /\d/.test(password);
  }

  togglePasswordVisibility(field: 'current' | 'new' | 'confirm'): void {
    switch (field) {
      case 'current': this.showCurrentPassword = !this.showCurrentPassword; break;
      case 'new': this.showNewPassword = !this.showNewPassword; break;
      case 'confirm': this.showConfirmPassword = !this.showConfirmPassword; break;
    }
  }

  isFormValid(): boolean {
    return !!(this.currentPassword?.trim() && 
           this.newPassword?.trim() && 
           this.confirmPassword?.trim() &&
           this.newPassword === this.confirmPassword &&
           this.validatePassword(this.newPassword).isValid);
  }

  changePassword(): void {
    if (this.isBlocked || !this.isFormValid()) {
      return;
    }

    // Verificar datos de usuario
    const userRes = localStorage.getItem('user');
    if (!userRes) {
      this.notificationService.showError('Sesión expirada. Inicia sesión nuevamente.');
      this.router.navigate(['/login']);
      return;
    }

    let userData;
    try {
      userData = JSON.parse(userRes);
    } catch {
      this.notificationService.showError('Error en los datos de usuario. Inicia sesión nuevamente.');
      this.router.navigate(['/login']);
      return;
    }

    if (!userData.cognito || !userData.email) {
      this.notificationService.showError('Datos de usuario incompletos');
      return;
    }

    this.loaderService.show();

    this.userService.updatePassword(userData.cognito, this.newPassword, userData.email, this.currentPassword)
      .subscribe({
        next: (response) => {
          this.loaderService.hide();
          if (response?.success && response?.code === 200) {
            this.notificationService.showSuccess(
              'Contraseña actualizada exitosamente'
            );
            this.clearForm();
          } else {
            this.handleError('Error al actualizar la contraseña');
          }
        },
        error: (error) => {
          this.loaderService.hide();
          if (error.status === 401) {
            this.notificationService.showError('Sesión expirada. Inicia sesión nuevamente.');
            this.router.navigate(['/login']);
            return;
          }
          const message = error?.error?.message || 'Error de conexión';
          this.handleError(message);
        }
      });
  }

  private handleError(message: string): void {
    this.attempts++;
    
    if (this.attempts >= this.maxAttempts) {
      this.isBlocked = true;
      this.notificationService.showError(
        'Demasiados intentos fallidos',
        'Intenta nuevamente en 15 minutos'
      );
      setTimeout(() => {
        this.isBlocked = false;
        this.attempts = 0;
      }, 900000); // 15 minutos
    } else {
      this.notificationService.showError(message);
    }
  }

  private clearForm(): void {
    this.currentPassword = '';
    this.newPassword = '';
    this.confirmPassword = '';
  }
}